import React, { useState, useEffect } from "react";
import LayoutAccountant from "../../components/Layout";
import { Button, Table, message, Input } from "antd";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { http } from "../../services/http";
import moment from "moment";
import { numberWithSpacesIntl } from "../../utils/numberWithSpaces";
import { useQuery } from "@tanstack/react-query";
import { checkValueToNotNull } from "../../utils/checkValue";
import { Trash2, Pencil } from "lucide-react";
import AddConversionModal from "../../components/Modal/AddConversion";
import EditConversionModal from "../../components/Modal/EditConversion";
import { removeSpaces } from "../../utils/formatMoney";

const ConversionAccountant = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const code = location.pathname.split("/")[2];
  const menuName = localStorage.getItem("menuName") ?? "";

  const [btnLoading, setBtnLoading] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [loadingData, setLoadingData] = useState([]);

  const [docType] = useState("");
  const [bpName] = useState("");
  const [currentPage] = useState(0); //Paginationsiz ishlashiga kelishilgan
  const pageSize = "1000"; //Paginationsiz ishlashiga kelishilgan
  const status = "NOT_UPLOADED"; //Status

  // We will map data and put some values we need
  const fetchData = async ({ queryKey }) => {
    const [_, docType, status, bpOrAcctName, pageSize, skip, accCode] =
      queryKey;
    try {
      const { data } = await http.get(
        `api/convertations/drafts?docType=${docType}&status=${status}&bpOrAcctName=${bpOrAcctName}&bpOrAcctCode=${bpOrAcctName}&skip=${skip}&pageSize=${pageSize}`,
      );
      const mappedData = data.data.map((item) => {
        const rubRate =
          item.docCur === "RUB" ? item.intConv / item.paymentSumBp : "";
        const crossRate =
          item.docCur === "RUB" ? item.sapUsdToUzsRate / rubRate : "";
        const usdRate =
          item.docCur === "USD"
            ? item.intConv / item.paymentSumBp
            : item.sapUsdToUzsRate;
        const paymSumCrosUsd =
          item.docCur === "RUB"
            ? item.paymentSumBp / item.crossRate
            : item.paymentSumBp;

        return {
          ...item,
          rubRate: rubRate,
          crossRate: crossRate,
          usdRate: usdRate,
          paymSumCrosUsd: paymSumCrosUsd,
        };
      });
      return mappedData;
    } catch (err) {
      console.error(err?.response?.data?.message);
    }
  };

  const {
    data = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      "convertations",
      docType,
      status,
      bpName,
      pageSize,
      currentPage,
      code, //accCode
    ],
    queryFn: fetchData,
    keepPreviousData: true,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (data.length > 0) {
      setLoadingData(Array(data.length).fill(false));
    }
  }, [data]);

  const columns = [
    {
      title: t("paymentDate"),
      dataIndex: "docDate",
      key: "docDate",
      render: (value) =>
        checkValueToNotNull(value, "-", moment(value).format("DD-MM-YYYY")),
    },
    {
      title: t("paymentType"),
      dataIndex: "docType",
      key: "docType",
      render: (value) => switchDocType(value),
    },
    {
      title: t("bp/Acc"),
      dataIndex: "bpOrAcctName",
      key: "bpOrAcctName",
    },
    {
      title: t("convertationCurrency"),
      dataIndex: "docCur",
      key: "docCur",
    },
    {
      title: t("paidSumConvertation"),
      dataIndex: "intConv",
      key: "intConv",
      render: (text, record) => {
        return (
          <span>{`${numberWithSpacesIntl(text)} ${switchCurrency("SUM")}`}</span>
        );
      },
    },
    {
      title: t("paidSumBp"),
      dataIndex: "paymentSumBp",
      key: "paymentSumBp",
      render: (text, record) => {
        return (
          <span>{`${numberWithSpacesIntl(text)} ${switchCurrency(record.docCur)}`}</span>
        );
      },
    },
    {
      title: t("converRateRUB"),
      dataIndex: "rubRate",
      key: "rubRate",
      render: (text, record) => {
        return (
          <span>
            {checkValueToNotNull(
              text,
              "",
              `${numberWithSpacesIntl(text)} ${switchCurrency("SUM")}`,
            )}
          </span>
        );
      },
    },
    {
      title: t("converCrossRate"),
      dataIndex: "crossRate",
      key: "crossRate",
      render: (text, record) => {
        return (
          <span>
            {checkValueToNotNull(
              text,
              "",
              `${numberWithSpacesIntl(text)} ${switchCurrency("RUB")}`,
            )}
          </span>
        );
      },
    },
    {
      title: t("converUsdRate"),
      dataIndex: "usdRate",
      key: "usdRate",
      render: (text, record) => {
        return (
          <span>
            {checkValueToNotNull(
              text,
              "",
              `${numberWithSpacesIntl(text)} ${switchCurrency("SUM")}`,
            )}
          </span>
        );
      },
    },
    {
      title: t("paymSumCrosUsd"),
      dataIndex: "paymSumCrosUsd",
      key: "paymSumCrosUsd",
      render: (text, record) => {
        return (
          <span>
            {checkValueToNotNull(
              text,
              "",
              `${numberWithSpacesIntl(text)} ${switchCurrency("USD")}`,
            )}
          </span>
        );
      },
    },
    {
      title: t("sapUSDUZSRate"),
      dataIndex: "sapUsdToUzsRate",
      key: "sapUsdToUzsRate",
      render: (text, record) => {
        return (
          <span>
            {checkValueToNotNull(
              text,
              "",
              `${numberWithSpacesIntl(text)} ${switchCurrency("SUM")}`,
            )}
          </span>
        );
      },
    },
    {
      title: t("sapUSDRUBRate"),
      dataIndex: "sapUsdToRubRate",
      key: "sapUsdToRubRate",
      render: (text, record) => {
        return (
          <span>
            {checkValueToNotNull(
              text,
              "",
              `${numberWithSpacesIntl(text)} ${switchCurrency("RUB")}`,
            )}
          </span>
        );
      },
    },
    {
      title: t("status"),
      dataIndex: "status",
      key: "status",
      render: (value) => switchStatus(value),
    },
    {
      title: t(""),
      dataIndex: "action",
      key: "action",
      render: (text, record, index) => {
        return (
          <div className="flex gap-2">
            <Button
              type="primary"
              onClick={() => handleEditModalClick(record)}
              loading={btnLoading}
            >
              <Pencil />
            </Button>
            <Button
              danger
              type="primary"
              onClick={() => handleDeleteBtnClick(record, index)}
              loading={loadingData[index]}
            >
              {!loadingData[index] && <Trash2 />}
            </Button>
          </div>
        );
      },
    },
  ];

  const handleUploadToSap = async (data) => {
    setBtnLoading(true);

    const postData = data.map((item) => {
      return {
        docEntry: item.docEntry,
        docType: item.docType,
        bpOrAcctCode: item.bpOrAcctCode,
        docCur: item.docCur,
        intConv: removeSpaces(item.intConv),
        paymentSumBp: removeSpaces(item.paymentSumBp),
        crossRate: item.docCur === "RUB" ? removeSpaces(item.crossRate) : 0,
        usdRate: removeSpaces(item.usdRate),
        docDate: moment(item.docDate).format("YYYY-MM-DD"),
      };
    });

    try {
      await http.post("api/convertations/drafts/upload-to-sap", postData);
      message.success(t("successfullyUploaded"));
      refetch();
    } catch (error) {
      message.error(error?.response?.data?.message);
    } finally {
      setBtnLoading(false);
    }
  };

  const switchCurrency = (currency) => {
    switch (currency) {
      case "SUM":
        return "so'm";
      case "USD":
        return "$";
      case "EUR":
        return "€";
      case "RUB":
        return "₽";
      default:
        return "so'm";
    }
  };

  const switchDocType = (docType) => {
    switch (docType) {
      case "S":
        return t("supplier");
      case "A":
        return t("account");
      default:
        return docType;
    }
  };

  const switchStatus = (status) => {
    switch (status) {
      case "NOT_UPLOADED":
        return t("notUploaded");
      case "uploaded":
        return t("uploaded");
      default:
        return status;
    }
  };

  const handleEditModalClick = (record) => {
    setIsEditModalVisible(true);
    setSelectedRowData(record);
  };

  const handleDeleteBtnClick = async (record, index) => {
    const newLoading = [...loadingData];
    newLoading[index] = true;
    setLoadingData(newLoading);

    const patchingData = [
      {
        docEntry: record?.docEntry,
      },
    ];

    try {
      await http.delete(`api/convertations/drafts`, {
        data: patchingData,
        headers: {
          "Content-Type": "application/json", // optional, just in case
        },
      });
      refetch();
    } catch (err) {
      message.error(err?.response?.data?.message);
      newLoading[index] = false;
      setLoadingData(newLoading);
    } finally {
      newLoading[index] = false;
      setLoadingData(newLoading);
    }
  };

  const handleAddClick = () => {
    setIsAddModalVisible(true);
  };

  const handleAddModalClose = () => {
    setIsAddModalVisible(false);
  };

  const handleCloseEditModal = () => {
    setIsEditModalVisible(false);
  };

  const intConvTotal = data.reduce(
    (sum, item) => sum + Number(removeSpaces(item.intConv)),
    0,
  );

  return (
    <LayoutAccountant>
      <div className="p-5 px-10">
        <h1 className="text-2xl font-bold text-basic">
          {t("Konvertatsiya")}-{code}-{menuName}
        </h1>

        <div className="mt-10 rounded-lg border-t-4 border-t-basic bg-white drop-shadow-md">
          <div className="p-5">
            {/* Upload To Sap Button */}
            <div className="mt-5 flex items-center justify-end">
              <Button
                type="primary"
                loading={btnLoading}
                className="bg h-9 w-auto bg-basic text-white hover:!bg-blue-900"
                onClick={() => {
                  handleUploadToSap(data);
                }}
                disabled={!data.length}
              >
                {t("uploadToSAP")}
              </Button>
            </div>
            {/* Table */}
            <div className="mt-5 overflow-x-auto">
              <Table
                columns={columns}
                dataSource={data}
                className="mt-10"
                pagination={false}
                scroll={{ x: "max-content" }}
                loading={isLoading}
                rowKey={"docEntry"}
              />
            </div>

            {/* Add Button */}
            <div className="mt-9 flex items-center justify-end gap-10">
              <div className="flex items-center gap-5">
                <span className="font-semibold">{t("conversionTotal")} :</span>
                <Input
                  value={numberWithSpacesIntl(intConvTotal)}
                  className="h-9 w-auto border-[1px] border-[#D0D0D0]"
                  suffix={switchCurrency("SUM")}
                  disabled
                />
              </div>
              <Button
                type="primary"
                loading={btnLoading}
                className="bg h-9 w-auto bg-basic text-white hover:!bg-blue-900"
                onClick={handleAddClick}
              >
                {t("add")}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <AddConversionModal
        visible={isAddModalVisible}
        data={data[0]}
        onClose={handleAddModalClose}
        refetch={refetch}
      />
      <EditConversionModal
        visible={isEditModalVisible}
        data={selectedRowData}
        onClose={handleCloseEditModal}
        refetch={refetch}
      />
    </LayoutAccountant>
  );
};

export default ConversionAccountant;
